import type {
  AiCompressedRetrievalSlice,
  AiRankedRetrievalRecord,
  AiRetrievalBundle,
  AiRetrievalDiagnostics,
  AiRetrievalRequest,
  AiRetrievalScope,
} from './retrievalTypes'
import { hashRetrievalText, retrievalCacheKey } from './retrievalCache'
import { summarizeCompressedSlices } from './retrievalCompression'

export const buildRetrievalDiagnostics = (input: {
  request: AiRetrievalRequest
  scopes: AiRetrievalScope[]
  records: AiRankedRetrievalRecord[]
  included: AiRankedRetrievalRecord[]
  slices: AiCompressedRetrievalSlice[]
  startedAt: number
  cacheKey?: string
  cacheHit: boolean
  cacheWriteAttempted: boolean
  cacheStale?: boolean
}): AiRetrievalDiagnostics => {
  const rawCharacterCount = input.records.reduce((sum, record) => sum + record.summary.length + record.title.length, 0)
  const compressedCharacterCount = input.slices.reduce((sum, slice) => sum + slice.text.length, 0)
  return {
    generatedAtIso: input.request.nowIso ?? new Date().toISOString(),
    elapsedMs: Math.max(0, Date.now() - input.startedAt),
    sourceCounts: input.records.reduce<AiRetrievalDiagnostics['sourceCounts']>((counts, record) => ({
      ...counts,
      [record.scope]: (counts[record.scope] ?? 0) + 1,
    }), {}),
    selectedScopes: input.scopes,
    rankedRecordCount: input.records.length,
    includedRecordCount: input.included.length,
    omittedRecordCount: Math.max(0, input.records.length - input.included.length),
    compression: {
      rawCharacterCount,
      compressedCharacterCount,
      sliceCount: input.slices.length,
    },
    cache: {
      key: input.cacheKey ?? retrievalCacheKey(input.request, input.scopes),
      hit: input.cacheHit,
      writeAttempted: input.cacheWriteAttempted,
      stale: Boolean(input.cacheStale),
    },
    topRanking: input.records.slice(0, 5).map((record) => ({
      id: record.id,
      scope: record.scope,
      score: record.ranking.score,
      reasons: record.ranking.factors.slice(0, 4).map((factor) => factor.reason),
    })),
  }
}

export const assembleRetrievalBundle = (input: {
  request: AiRetrievalRequest
  scopes: AiRetrievalScope[]
  rankedRecords: AiRankedRetrievalRecord[]
  slices: AiCompressedRetrievalSlice[]
  startedAt: number
  cacheKey?: string
  cacheHit?: boolean
  cacheWriteAttempted?: boolean
  cacheStale?: boolean
}): AiRetrievalBundle => {
  const maxRecords = input.request.limits?.maxRecords ?? 20
  const included = input.rankedRecords.slice(0, maxRecords)
  const summary = summarizeCompressedSlices(input.slices)
  const generatedAtIso = input.request.nowIso ?? new Date().toISOString()
  const diagnostics = buildRetrievalDiagnostics({
    request: input.request,
    scopes: input.scopes,
    records: input.rankedRecords,
    included,
    slices: input.slices,
    startedAt: input.startedAt,
    cacheKey: input.cacheKey,
    cacheHit: Boolean(input.cacheHit),
    cacheWriteAttempted: Boolean(input.cacheWriteAttempted),
    cacheStale: input.cacheStale,
  })

  return {
    workspaceId: input.request.workspaceId,
    queryHash: hashRetrievalText(input.request.query.trim().toLowerCase()),
    queryPreview: input.request.query.slice(0, 120),
    digest: {
      summary,
      includedRecordIds: included.map((record) => record.id),
      omittedReason: input.rankedRecords.length > included.length
        ? `Omitted ${input.rankedRecords.length - included.length} lower-ranked retrieval records.`
        : undefined,
      retrievalDiagnostics: diagnostics,
      generatedAtIso,
    },
    slices: input.slices,
    rankedRecords: included,
    diagnostics,
  }
}
